import { Grid, MenuItem, useTheme } from "@mui/material";
import { ReportAccessType } from "models/Reporting/ReportAccessControls";
import React from "react";
import { permissionOptions } from "./constants";
import { ShareDialogSelect } from "./ShareDialogSelect";

// Component for setting permissions for a user in the share dialog
export const PermissionSetter = ({
    permissionToSet,
    setPermissionToSet,
    onPermissionSet,
    removeOption = false,
    roleChangeDisabled = false,
    handleRemove,
    id,
}: {
    permissionToSet: ReportAccessType;
    setPermissionToSet: (value: ReportAccessType) => void;
    onPermissionSet?: (value: ReportAccessType) => void;
    removeOption?: boolean;
    // When true, the role options (Owner/Edit/View) are shown as a single
    // disabled item and cannot be changed; only Remove is actionable. Used for
    // the impersonated dashboard owner, where the backend permits removal (for a
    // handoff) but still rejects role changes. (AUT-10497)
    roleChangeDisabled?: boolean;
    handleRemove?: () => void;
    id?: string;
}) => {
    const theme = useTheme();
    return (
        <Grid
            sx={{
                display: "flex",
                alignItems: "center",
            }}
            onClick={(e) => {
                e.stopPropagation();
            }}
        >
            <ShareDialogSelect<ReportAccessType>
                value={permissionToSet}
                onChange={(value) => {
                    if (value === ReportAccessType.Remove) {
                        if (handleRemove) {
                            handleRemove();
                        }
                        return;
                    }
                    setPermissionToSet(value);
                    onPermissionSet?.(value);
                }}
                id={id}
            >
                {roleChangeDisabled ? (
                    // Keep the current role visible as the Select's value, but
                    // disable it so it can't be changed — only Remove is offered.
                    <MenuItem
                        id={id ? `${id}-${permissionToSet}` : undefined}
                        key="current-role"
                        value={permissionToSet}
                        disabled
                        sx={{
                            fontWeight: theme.typography.fontWeightSemibold,
                            color: theme.palette.gray[600],
                            fontSize: "14px",
                            "&.Mui-disabled": {
                                opacity: 1,
                            },
                        }}
                    >
                        {
                            permissionOptions.find(
                                (option) => option.value === permissionToSet,
                            )?.label
                        }
                    </MenuItem>
                ) : (
                    permissionOptions.map((option) => (
                        <MenuItem
                            id={id ? `${id}-${option.value}` : undefined}
                            key={option.label}
                            value={option.value}
                            sx={{
                                fontWeight: theme.typography.fontWeightSemibold,
                                color:
                                    permissionToSet === option.value
                                        ? theme.palette.primary.main
                                        : theme.palette.gray[600],
                                fontSize: "14px",
                                "&:hover": {
                                    backgroundColor: theme.palette.primary.light,
                                    color: theme.palette.primary.main,
                                },
                            }}
                        >
                            {option.label}
                        </MenuItem>
                    ))
                )}
                {removeOption && (
                    <MenuItem
                        value={ReportAccessType.Remove}
                        id={id ? `${id}-remove` : undefined}
                        sx={{
                            fontWeight: theme.typography.fontWeightSemibold,
                            color: theme.palette.gray[600],
                            fontSize: "14px",
                            "&:hover": {
                                backgroundColor: theme.palette.primary.light,
                                color: theme.palette.primary.main,
                            },
                            borderTop: `1px solid ${theme.palette.gray[200]}`,
                        }}
                    >
                        Remove
                    </MenuItem>
                )}
            </ShareDialogSelect>
        </Grid>
    );
};
